import React, { useState, useRef } from 'react';
import { useForm } from '../../context/FormContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Search, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { titleCaseCompany } from '../../lib/nameCase';

export const EmploymentDetailsStep = () => {
  const { formData, updateFormData } = useForm();
  const [searchingPostcode, setSearchingPostcode] = useState({});
  const [addressOptions, setAddressOptions] = useState({});
  // Refs per employment card so we can scroll a newly-added entry into view —
  // a common cause of "stuck on submit" was users accidentally clicking Add
  // and never seeing the empty extra card sitting below the fold.
  const entryRefs = useRef({});

  // Debug logging
  console.log('EmploymentDetailsStep - formData.employment_history:', JSON.stringify(formData.employment_history, null, 2));
  console.log('EmploymentDetailsStep - formData.employment_years:', formData.employment_years);

  const needsEmployerDetails = ['Full-Time Employment', 'Part-Time Employment', 'Self-Employed', 'Temporary/Contract', 'Armed Forces'].includes(formData.employment_status);

  // Initialize employment history if not exists
  // Also merge employer_address from formData if employment_history item's address is empty
  const rawEmploymentHistory = formData.employment_history || [{
    employer: formData.employer || '',
    job_title: formData.job_title || '',
    employment_status: formData.employment_status || '',
    years: formData.employment_years ?? 0,
    months: formData.employment_months ?? 0,
    employer_phone: formData.employer_phone || '',
    employer_address_same_as_home: formData.employer_address_same_as_home || false,
    employer_address: formData.employer_address || { postcode: '', building_number: '', street: '', town: '' }
  }];
  
  console.log('EmploymentDetailsStep - rawEmploymentHistory:', JSON.stringify(rawEmploymentHistory, null, 2));
  
  // Ensure all fields are properly set for each employment entry
  const employmentHistory = rawEmploymentHistory.map((emp, index) => {
    const empAddress = emp.employer_address;
    const hasValidAddress = empAddress && (empAddress.postcode || empAddress.building_number || empAddress.street || empAddress.town);
    
    // Handle years/months - use nullish coalescing to handle 0 values correctly
    const years = emp.years !== undefined && emp.years !== null ? emp.years : (index === 0 ? formData.employment_years : 0) ?? 0;
    const months = emp.months !== undefined && emp.months !== null ? emp.months : (index === 0 ? formData.employment_months : 0) ?? 0;
    
    console.log(`EmploymentDetailsStep - emp[${index}].years:`, emp.years, 'computed years:', years);
    
    return {
      ...emp,
      employer: emp.employer || (index === 0 ? formData.employer : '') || '',
      job_title: emp.job_title || (index === 0 ? formData.job_title : '') || '',
      employer_phone: emp.employer_phone || (index === 0 ? formData.employer_phone : '') || '',
      years: years,
      months: months,
      employer_address: hasValidAddress ? empAddress : (index === 0 ? formData.employer_address : null) || { postcode: '', building_number: '', street: '', town: '' }
    };
  });
  
  console.log('EmploymentDetailsStep - final employmentHistory:', JSON.stringify(employmentHistory, null, 2));

  const updateEmployment = (index, field, value) => {
    const newHistory = [...employmentHistory];
    newHistory[index] = { ...newHistory[index], [field]: value };
    
    // Auto-populate employer as "Self Employed" when self-employed is selected
    if (field === 'employment_status' && value === 'Self-Employed' && !newHistory[index].employer) {
      newHistory[index].employer = 'Self Employed';
    }
    
    updateFormData({ employment_history: newHistory });
    
    // Also update main fields for backward compatibility (for first employment)
    if (index === 0) {
      if (field === 'employer') updateFormData({ employer: value });
      if (field === 'job_title') updateFormData({ job_title: value });
      if (field === 'years') updateFormData({ employment_years: value });
      if (field === 'months') updateFormData({ employment_months: value });
      if (field === 'employer_phone') updateFormData({ employer_phone: value });
      if (field === 'employer_address_same_as_home') updateFormData({ employer_address_same_as_home: value });
      if (field === 'employer_address') updateFormData({ employer_address: value });
      
      // Also auto-populate main employer field when self-employed
      if (field === 'employment_status' && value === 'Self-Employed' && !formData.employer) {
        updateFormData({ employer: 'Self Employed' });
      }
    }
  };

  const updateEmployerAddress = (index, field, value) => {
    const newHistory = [...employmentHistory];
    newHistory[index] = {
      ...newHistory[index],
      employer_address: {
        ...newHistory[index].employer_address,
        [field]: value
      }
    };
    updateFormData({ employment_history: newHistory });
    
    // Also update main employer_address for first employment
    if (index === 0) {
      updateFormData({
        employer_address: {
          ...formData.employer_address,
          [field]: value
        }
      });
    }
  };

  const handleSameAsHome = (index, checked) => {
    if (checked && formData.addresses && formData.addresses[0]) {
      const homeAddr = formData.addresses[0];
      const newAddress = {
        postcode: homeAddr.postcode || '',
        building_number: homeAddr.house_number || '',
        street: homeAddr.street || '',
        town: homeAddr.town || ''
      };
      
      const newHistory = [...employmentHistory];
      newHistory[index] = {
        ...newHistory[index],
        employer_address_same_as_home: checked,
        employer_address: newAddress
      };
      updateFormData({ employment_history: newHistory });
      
      if (index === 0) {
        updateFormData({
          employer_address_same_as_home: checked,
          employer_address: newAddress
        });
      }
    } else {
      const newHistory = [...employmentHistory];
      newHistory[index] = {
        ...newHistory[index],
        employer_address_same_as_home: checked,
        employer_address: { postcode: '', building_number: '', street: '', town: '' }
      };
      updateFormData({ employment_history: newHistory });
      
      if (index === 0) {
        updateFormData({
          employer_address_same_as_home: checked,
          employer_address: { postcode: '', building_number: '', street: '', town: '' }
        });
      }
    }
  };

  const addEmployment = () => {
    const newIndex = employmentHistory.length;
    updateFormData({
      employment_history: [...employmentHistory, {
        employer: '',
        job_title: '',
        employment_status: '',
        years: 0,
        months: 0,
        employer_phone: '',
        employer_address_same_as_home: false,
        employer_address: { postcode: '', building_number: '', street: '', town: '' }
      }]
    });
    // Scroll the newly-added entry into view on the next frame, so users
    // can't miss it. Toast doubles as an explicit confirmation.
    toast.success('Previous employment added — fill in the new entry below, or use Remove if added by mistake.');
    setTimeout(() => {
      const node = entryRefs.current[newIndex];
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const removeEmployment = (index) => {
    if (employmentHistory.length > 1) {
      const newHistory = employmentHistory.filter((_, i) => i !== index);
      updateFormData({ employment_history: newHistory });
    }
  };

  const lookupEmployerPostcode = async (index) => {
    const postcode = employmentHistory[index]?.employer_address?.postcode;
    if (!postcode || postcode.length < 5) {
      toast.error('Please enter a valid postcode');
      return;
    }

    setSearchingPostcode({ ...searchingPostcode, [index]: true });
    
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/address-lookup/${encodeURIComponent(postcode)}`);
      if (response.data.success && response.data.addresses?.length > 0) {
        setAddressOptions({ ...addressOptions, [index]: response.data.addresses });
        toast.success(`Found ${response.data.addresses.length} addresses`);
      } else {
        toast.error(response.data.error || 'No addresses found for this postcode');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Postcode lookup failed. Please check and try again.';
      toast.error(msg);
    } finally {
      setSearchingPostcode({ ...searchingPostcode, [index]: false });
    }
  };

  const selectEmployerAddress = (index, selectedAddress) => {
    const newHistory = [...employmentHistory];
    newHistory[index] = {
      ...newHistory[index],
      employer_address: {
        ...newHistory[index].employer_address,
        building_number: selectedAddress.building_number || selectedAddress.building_name || '',
        street: selectedAddress.street || '',
        town: selectedAddress.town || ''
      }
    };
    updateFormData({ employment_history: newHistory });
    if (index === 0) {
      updateFormData({
        employer_address: {
          ...formData.employer_address,
          building_number: selectedAddress.building_number || selectedAddress.building_name || '',
          street: selectedAddress.street || '',
          town: selectedAddress.town || ''
        }
      });
    }
    setAddressOptions({ ...addressOptions, [index]: null });
    toast.success('Address selected');
  };

  // Calculate total years of employment history
  const totalYears = employmentHistory.reduce((sum, emp) => sum + (emp.years || 0) + ((emp.months || 0) / 12), 0);
  const needsMoreHistory = totalYears < 3;

  return (
    <div className="space-y-6" data-testid="employment-details-step">
      <div className="text-center">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Employment Details
        </h2>
        <p className="text-slate-600 hidden md:block">
          {needsEmployerDetails 
            ? "We need 3 years of employment history. We never contact your employer." 
            : "How long have you been in this status?"}
        </p>
      </div>

      {employmentHistory.map((employment, index) => {
        // Flag incomplete additional entries so users can spot accidental adds.
        // First entry (index 0) is always "current employment" so never flagged.
        const isIncompleteExtra = index > 0 && (
          !employment.employment_status ||
          (['Full-Time Employment', 'Part-Time Employment', 'Self-Employed', 'Temporary/Contract', 'Armed Forces'].includes(employment.employment_status) &&
            (!employment.employer?.trim() || !employment.job_title?.trim())) ||
          ((employment.years || 0) === 0 && (employment.months || 0) === 0)
        );
        return (
        <div
          key={index}
          ref={(el) => { entryRefs.current[index] = el; }}
          className={`p-4 border rounded-lg space-y-4 ${isIncompleteExtra ? 'border-amber-400 bg-amber-50/40' : 'border-slate-200'}`}
          data-testid={`employment-${index}`}
        >
          <div className="flex justify-between items-center">
            <h3 className="font-medium text-slate-700">
              {index === 0 ? 'Current Employment' : `Previous Employment ${index}`}
            </h3>
            {index > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => removeEmployment(index)}
                className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                data-testid={`remove-employment-${index}`}
              >
                <Trash2 className="w-4 h-4" />
                Remove
              </Button>
            )}
          </div>

          {isIncompleteExtra && (
            <div
              className="text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded-md px-3 py-2"
              data-testid={`employment-${index}-incomplete-hint`}
            >
              This previous employment entry is empty. Either complete it or click <strong>Remove</strong> above if it was added by mistake.
            </div>
          )}

          {index > 0 && (
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Employment Status <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={employment.employment_status} 
                onValueChange={(value) => updateEmployment(index, 'employment_status', value)}
              >
                <SelectTrigger className="h-12" data-testid={`employment-status-${index}`}>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {['Full-Time Employment', 'Part-Time Employment', 'Self-Employed', 'Temporary/Contract', 'Retired', 'Homemaker', 'Carer', 'Education', 'Benefits', 'Armed Forces'].map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {(index === 0 ? needsEmployerDetails : ['Full-Time Employment', 'Part-Time Employment', 'Self-Employed', 'Temporary/Contract', 'Armed Forces'].includes(employment.employment_status)) && (
            <>
              <div>
                <Label htmlFor={`employer-${index}`} className="text-sm font-medium text-slate-700 mb-2 block">
                  {employment.employment_status === 'Self-Employed' ? 'Business Name' : 'Employer'} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id={`employer-${index}`}
                  placeholder={employment.employment_status === 'Self-Employed' ? 'Your business name' : 'Company name'}
                  value={employment.employer}
                  onChange={(e) => updateEmployment(index, 'employer', e.target.value)}
                  onBlur={(e) => {
                    const cased = titleCaseCompany(e.target.value);
                    if (cased !== e.target.value) updateEmployment(index, 'employer', cased);
                  }}
                  className="h-12"
                  data-testid={`employer-input-${index}`}
                />
              </div>

              <div>
                <Label htmlFor={`job_title-${index}`} className="text-sm font-medium text-slate-700 mb-2 block">
                  Job Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id={`job_title-${index}`}
                  placeholder="Your job title"
                  value={employment.job_title}
                  onChange={(e) => updateEmployment(index, 'job_title', e.target.value)}
                  className="h-12"
                  data-testid={`job-title-input-${index}`}
                />
              </div>

              <div>
                <Label htmlFor={`employer_phone-${index}`} className="text-sm font-medium text-slate-700 mb-2 block">
                  Employer Phone Number <span className="text-slate-400 text-xs">(optional)</span>
                </Label>
                <Input
                  id={`employer_phone-${index}`}
                  type="tel"
                  placeholder="e.g. 02071234567"
                  value={employment.employer_phone}
                  onChange={(e) => {
                    // Strip spaces, dashes, brackets, dots as user types — keeps the
                    // payload broker-API-safe (Artis rejects "01707 372858" with a space).
                    const cleaned = e.target.value.replace(/[\s\-().\\/]/g, '');
                    updateEmployment(index, 'employer_phone', cleaned);
                  }}
                  className="h-12"
                  data-testid={`employer-phone-input-${index}`}
                />
              </div>

              {/* Employer Address Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-sm font-medium text-slate-700">
                    Employer Address <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`same_as_home-${index}`}
                      checked={employment.employer_address_same_as_home}
                      onCheckedChange={(checked) => handleSameAsHome(index, checked)}
                      data-testid={`same-as-home-checkbox-${index}`}
                    />
                    <Label htmlFor={`same_as_home-${index}`} className="text-sm text-slate-600 cursor-pointer">
                      Same as home address
                    </Label>
                  </div>
                </div>

                {/* Employer Address - always show fields, but read-only when same as home */}
                <div className="space-y-3">
                  {/* Postcode Lookup */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Postcode</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="e.g. SW1A 1AA"
                        value={employment.employer_address?.postcode || ''}
                        onChange={(e) => updateEmployerAddress(index, 'postcode', e.target.value.toUpperCase())}
                        className={`h-11 flex-1 ${employment.employer_address_same_as_home ? 'bg-slate-100' : ''}`}
                        disabled={employment.employer_address_same_as_home}
                        data-testid={`employer-postcode-input-${index}`}
                      />
                      {!employment.employer_address_same_as_home && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => lookupEmployerPostcode(index)}
                          disabled={searchingPostcode[index]}
                          className="h-11 px-4"
                          data-testid={`lookup-employer-postcode-${index}`}
                        >
                          {searchingPostcode[index] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Search className="w-4 h-4 mr-1" />
                              Find
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    
                    {/* Address selection dropdown */}
                    {addressOptions[index] && addressOptions[index].length > 0 && (
                      <div className="mt-2 rounded-lg border-2 border-blue-400 bg-blue-50/60 p-3 shadow-sm ring-2 ring-blue-200/70">
                        <Label className="text-sm font-semibold text-blue-800 mb-1 block">
                          Select employer address ({addressOptions[index].length} found)
                        </Label>
                        <p className="text-xs text-blue-700/80 mb-2">
                          Please pick the address from the list to auto-fill the fields below.
                        </p>
                        <select
                          className="w-full h-11 px-3 rounded-lg border-2 border-blue-400 bg-white hover:border-blue-500 focus:border-blue-600 focus:ring-2 focus:ring-blue-300 outline-none text-sm"
                          defaultValue=""
                          onChange={(e) => {
                            const addr = addressOptions[index][parseInt(e.target.value)];
                            if (addr) selectEmployerAddress(index, addr);
                          }}
                          data-testid={`employer-address-select-${index}`}
                        >
                          <option value="" disabled>Tap to choose the address...</option>
                          {addressOptions[index].map((addr, i) => (
                            <option key={i} value={i}>{addr.full}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Building Number/Name</Label>
                    <Input
                      placeholder="e.g. 10"
                      value={employment.employer_address?.building_number || ''}
                      onChange={(e) => updateEmployerAddress(index, 'building_number', e.target.value)}
                      className={`h-11 ${employment.employer_address_same_as_home ? 'bg-slate-100' : ''}`}
                      disabled={employment.employer_address_same_as_home}
                      data-testid={`employer-building-input-${index}`}
                    />
                  </div>

                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Street</Label>
                    <Input
                      placeholder="Street name"
                      value={employment.employer_address?.street || ''}
                      onChange={(e) => updateEmployerAddress(index, 'street', e.target.value)}
                      className={`h-11 ${employment.employer_address_same_as_home ? 'bg-slate-100' : ''}`}
                      disabled={employment.employer_address_same_as_home}
                      data-testid={`employer-street-input-${index}`}
                    />
                  </div>

                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Town/City</Label>
                    <Input
                      placeholder="Town or city"
                      value={employment.employer_address?.town || ''}
                      onChange={(e) => updateEmployerAddress(index, 'town', e.target.value)}
                      className={`h-11 ${employment.employer_address_same_as_home ? 'bg-slate-100' : ''}`}
                      disabled={employment.employer_address_same_as_home}
                      data-testid={`employer-town-input-${index}`}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <div>
            <Label className="text-sm font-medium text-slate-700 mb-3 block">
              How long {index === 0 ? (needsEmployerDetails ? 'have you worked there' : 'have you been in this status') : 'were you there'}? <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Years</Label>
                <Select 
                  value={String(employment.years ?? 0)} 
                  onValueChange={(value) => updateEmployment(index, 'years', parseInt(value))}
                >
                  <SelectTrigger className="h-12" data-testid={`employment-years-select-${index}`}>
                    <SelectValue>{String(employment.years ?? 0)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(51)].map((_, i) => (
                      <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Months</Label>
                <Select 
                  value={String(employment.months ?? 0)} 
                  onValueChange={(value) => updateEmployment(index, 'months', parseInt(value))}
                >
                  <SelectTrigger className="h-12" data-testid={`employment-months-select-${index}`}>
                    <SelectValue>{String(employment.months ?? 0)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => (
                      <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
        );
      })}

      {needsMoreHistory && (
        <div className="text-center">
          <p className="text-sm text-amber-600 mb-3">
            You need {(3 - totalYears).toFixed(1)} more years of employment history
          </p>
          <Button
            variant="outline"
            onClick={addEmployment}
            className="gap-2"
            data-testid="add-employment-btn"
          >
            <Plus className="w-4 h-4" />
            Add Previous Employment
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmploymentDetailsStep;
